/**
 * Czysta logika puzzle (WP12e) — bez Phaser.
 * Drag&snap na siatkę 3×3 / 4×4; układ zawsze możliwy (każdy klocek ma dom).
 * Brak fail-state: gwiazdki tylko rosną (0 → 1 po ukończeniu).
 */

export type GridSize = 3 | 4;

export type SlotPos = { col: number; row: number };

export type PuzzleLayout = {
  size: GridSize;
  pieceCount: number;
  /** Id klocków 0..n²-1 w losowej kolejności (taca startowa). */
  trayOrder: number[];
};

export type SnapResult =
  | { ok: true; slot: number }
  | { ok: false; reason: 'wrong_slot' | 'occupied' | 'too_far' };

/** Pastelowe kolory klocków (hex RGB). */
export const PIECE_COLORS: readonly number[] = [
  0xffb6c1, // róż
  0xbde0fe, // błękit
  0xc9b1ff, // fiolet
  0xb8e6d0, // mięta
  0xffe4a0, // żółty
  0xffc8a2, // brzoskwiniowy
  0xa0e7e5, // turkus
  0xf4a6c8, // różowy
  0xc5e1a5, // zieleń
  0xd4a5ff, // lawenda
  0xffd6a5, // morela
  0x9bf6ff, // cyjan
  0xfdffb6, // cytryna
  0xffadad, // koral
  0xcaffbf, // limonka
  0xbdb2ff, // perwinkle
] as const;

/**
 * Emoji na klockach — legacy / ghost podgląd slotu.
 * WAVE7-D: klocki to cropy z obrazka (patrz puzzleArt.ts).
 */
export const PIECE_EMOJIS: readonly string[] = [
  '🐶',
  '🐱',
  '⭐',
  '🦴',
  '🌸',
  '🎈',
  '🧸',
  '🌈',
  '🍎',
  '🦋',
  '☀️',
  '🌙',
  '🍓',
  '🎵',
  '💎',
  '🏡',
] as const;

/** Prosty crop prostokąt klocka w siatce (unit-testowalny). */
export function pieceCropRect(
  pieceId: number,
  size: GridSize,
  imageW: number,
  imageH: number,
): { x: number; y: number; w: number; h: number } {
  const { col, row } = posOf(pieceId, size);
  const w = Math.floor(imageW / size);
  const h = Math.floor(imageH / size);
  return { x: col * w, y: row * h, w, h };
}

export function isGridSize(n: number): n is GridSize {
  return n === 3 || n === 4;
}

export function pieceCount(size: GridSize): number {
  return size * size;
}

export function slotOf(col: number, row: number, size: GridSize): number {
  return row * size + col;
}

export function posOf(slot: number, size: GridSize): SlotPos {
  return { col: slot % size, row: Math.floor(slot / size) };
}

/** Docelowy slot klocka = jego id (0 w lewym górnym rogu). */
export function correctSlot(pieceId: number): number {
  return pieceId;
}

export function colorForPiece(pieceId: number): number {
  return PIECE_COLORS[pieceId % PIECE_COLORS.length]!;
}

export function emojiForPiece(pieceId: number): string {
  return PIECE_EMOJIS[pieceId % PIECE_EMOJIS.length]!;
}

/**
 * Fisher–Yates; gwarantuje permutację (każdy klocek raz).
 * Układ zawsze „układalny" — nie sliding-puzzle, tylko snapy do domu.
 */
export function shuffleTray(ids: number[], rng: () => number = Math.random): number[] {
  const a = ids.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = a[i]!;
    a[i] = a[j]!;
    a[j] = tmp;
  }
  return a;
}

export function createPuzzle(
  size: GridSize,
  rng: () => number = Math.random,
): PuzzleLayout {
  const n = pieceCount(size);
  const ids = Array.from({ length: n }, (_, i) => i);
  return {
    size,
    pieceCount: n,
    trayOrder: shuffleTray(ids, rng),
  };
}

/**
 * Czy punkt upuszczenia jest wystarczająco blisko środka slotu.
 * thresholdPx — typowo ~ połowa boku klocka.
 */
export function isNearSlot(
  dropX: number,
  dropY: number,
  slotCenterX: number,
  slotCenterY: number,
  thresholdPx: number,
): boolean {
  const dx = dropX - slotCenterX;
  const dy = dropY - slotCenterY;
  return dx * dx + dy * dy <= thresholdPx * thresholdPx;
}

/**
 * Próba snapu: tylko do właściwego slotu klocka, jeśli wolny i blisko.
 * occupied — set zapełnionych slotów (id slotów).
 */
export function trySnap(
  pieceId: number,
  dropX: number,
  dropY: number,
  slotCenters: ReadonlyArray<{ x: number; y: number }>,
  occupied: ReadonlySet<number>,
  thresholdPx: number,
): SnapResult {
  const home = correctSlot(pieceId);
  const center = slotCenters[home];
  if (!center) return { ok: false, reason: 'wrong_slot' };
  if (!isNearSlot(dropX, dropY, center.x, center.y, thresholdPx)) {
    return { ok: false, reason: 'too_far' };
  }
  if (occupied.has(home)) {
    return { ok: false, reason: 'occupied' };
  }
  return { ok: true, slot: home };
}

/** Wszystkie klocki na swoich slotach. */
export function isComplete(
  placed: ReadonlyMap<number, number>,
  size: GridSize,
): boolean {
  const n = pieceCount(size);
  if (placed.size !== n) return false;
  for (let id = 0; id < n; id++) {
    if (placed.get(id) !== correctSlot(id)) return false;
  }
  return true;
}

/**
 * Gwiazdki puzzle: 0 w grze, 1 po ukończeniu (shared maxStars=1, good=ukończenie).
 * Tylko rosną — nigdy nie spadają.
 */
export function starsForState(complete: boolean, previousStars = 0): number {
  const next = complete ? 1 : 0;
  return Math.max(previousStars, next);
}

/** Score telemetrii (nie nagroda): klocki × 10 + bonus czasu. */
export function scoreForRun(
  placedCount: number,
  durationMs: number,
  complete: boolean,
): number {
  const base = placedCount * 10;
  if (!complete) return base;
  // im szybciej, tym więcej (soft, nie karze)
  const sec = Math.max(0, durationMs / 1000);
  const timeBonus = Math.max(0, Math.floor(120 - sec));
  return base + timeBonus + 50;
}

export type ReturnHub = 'School' | 'ConsoleHub' | 'Town';

/** Dokąd wrócić po grze / Reward (entryPoint z hubów WP11). */
export function returnSceneForEntry(entryPoint?: string): ReturnHub {
  if (entryPoint === 'console') return 'ConsoleHub';
  if (entryPoint === 'phone') return 'Town';
  return 'School';
}

/** Soft timer 60–90 s — brak fail, tylko informacja UI. */
export const ROUND_SOFT_MS = 75_000;
export const SNAP_THRESHOLD_RATIO = 0.55;
